import { pluralize } from "$root/utils";
import { IconButton } from "@mdotm/shadcn/mdotm/components";
import { Column } from "@mdotm/shadcn/legacy/components";
import type { GetContextMenuItems, GridApi, IRowNode, MenuItemDef } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { match } from "ts-pattern";
import type { CompositionRowType, MergedCompositionRow } from "../rows";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";

export type GroupChildrenSummary = {
	portfolioCount: number;
	instrumentCount: number;
	portfolioSubGroupCount: number;
};

type GroupCellContentProps = {
	label: string;
	rowType: CompositionRowType | undefined;
	portfolioName?: string;
	isin?: string;
	childrenSummary?: GroupChildrenSummary;
	onOpenContextMenu?: () => void;
};

type GroupCellLabelProps = {
	label: string;
	summary: string;
	weight?: "bold" | "semibold";
	onOpenContextMenu?: () => void;
};

function GroupCellLabel({ label, summary, weight = "bold", onOpenContextMenu }: GroupCellLabelProps) {
	return (
		<Column className="leading-tight">
			<span className={`${weight === "bold" ? "font-bold" : "font-semibold"} truncate`}>{label}</span>
			<span className="text-[11px] text-(--palette-N500)">{summary}</span>
			{onOpenContextMenu && (
				<IconButton
					icon="Vertical"
					className="absolute inset-y-0 right-1 transition-opacity opacity-0 group-hover:opacity-100"
					color={themeCSSVars.palette_N800}
					onClick={onOpenContextMenu}
				/>
			)}
		</Column>
	);
}

export function LabGroupCellContent({
	label,
	rowType,
	portfolioName,
	isin,
	childrenSummary,
	onOpenContextMenu,
}: GroupCellContentProps) {
	return match(rowType)
		.with("group", "subgroup", () => {
			const summary = [
				childrenSummary?.portfolioCount ? pluralize(childrenSummary.portfolioCount, "Portfolio") : "",
				childrenSummary?.instrumentCount ? pluralize(childrenSummary.instrumentCount, "Instrument") : "",
			]
				.filter(Boolean)
				.join(", ");
			return <GroupCellLabel label={label} summary={summary} onOpenContextMenu={onOpenContextMenu} />;
		})
		.with("single-instruments", () => {
			const summary = childrenSummary?.portfolioSubGroupCount
				? pluralize(childrenSummary.portfolioSubGroupCount, "Group")
				: "";
			return <GroupCellLabel label={label} summary={summary} onOpenContextMenu={onOpenContextMenu} />;
		})
		.with("portfolio", () => {
			const summary = [
				childrenSummary?.portfolioSubGroupCount ? pluralize(childrenSummary.portfolioSubGroupCount, "Group") : "",
				childrenSummary?.instrumentCount ? pluralize(childrenSummary.instrumentCount, "Instrument") : "",
			]
				.filter(Boolean)
				.join(", ");
			return <GroupCellLabel label={label} summary={summary} weight="semibold" onOpenContextMenu={onOpenContextMenu} />;
		})
		.with("instrument", () => (
			<Column className="leading-tight">
				<span className="truncate">{label}</span>
				{portfolioName ? (
					<span className="text-[11px] text-(--palette-N500)">via {portfolioName}</span>
				) : (
					isin && <span className="text-[11px] text-(--palette-N500)">{isin}</span>
				)}
			</Column>
		))
		.with(undefined, () => <span className="truncate">{label}</span>)
		.exhaustive();
}

export function LabGroupCellRenderer(params: CustomCellRendererProps<MergedCompositionRow>) {
	const data = params.data;
	const rowType = data ? Object.values(data.data).find((d) => d?.rowType != null)?.rowType : undefined;
	const label = data?.label ?? (params.value as string) ?? "";
	const children = params.node.childrenAfterFilter ?? [];

	const getChildRowType = (node: (typeof children)[number]) => {
		const d = node.data;
		return d ? Object.values(d.data).find((entry) => entry?.rowType != null)?.rowType : undefined;
	};

	const childrenSummary: GroupChildrenSummary = {
		portfolioCount: children.filter((c) => getChildRowType(c) === "portfolio").length,
		instrumentCount: children.filter((c) => getChildRowType(c) === "instrument").length,
		portfolioSubGroupCount: children.filter((c) => getChildRowType(c) === "subgroup").length,
	};

	const portfolioName = Object.values(data?.data ?? {}).find((d) => d?.portfolioName != null)?.portfolioName;
	const isin = Object.values(data?.data ?? {}).find((d) => d?.portfolioIdentifier?.ISIN != null)?.portfolioIdentifier
		?.ISIN;

	const hasChildGroups = (params.node.childrenAfterGroup ?? []).some((child) => child.group);
	const onOpenContextMenu = hasChildGroups
		? () => {
				params.api.showContextMenu({
					source: "api",
					rowNode: params.node,
					column: params.column,
					value: params.value,
				});
			}
		: undefined;

	return (
		<LabGroupCellContent
			label={label}
			rowType={rowType}
			childrenSummary={childrenSummary}
			portfolioName={portfolioName}
			isin={isin}
			onOpenContextMenu={onOpenContextMenu}
		/>
	);
}

function setGroupsUnderneathExpanded(
	node: IRowNode<MergedCompositionRow>,
	api: GridApi<MergedCompositionRow>,
	expanded: boolean,
): void {
	node.setExpanded(expanded);
	const stack: IRowNode<MergedCompositionRow>[] = [...(node.childrenAfterGroup ?? [])];
	while (stack.length > 0) {
		const child = stack.pop();
		if (child?.group) {
			child.setExpanded(expanded);
			stack.push(...(child.childrenAfterGroup ?? []));
		}
	}
	api.onGroupExpandedOrCollapsed();
}

export const labGroupCellContextMenuItems: GetContextMenuItems<MergedCompositionRow> = (params) => {
	const { node, api } = params;
	const items: MenuItemDef<MergedCompositionRow>[] = [];
	if (node?.group && (node.childrenAfterGroup ?? []).some((child) => child.group)) {
		items.push(
			{
				name: "Expand group",
				cssClasses: ["[&_.ag-menu-option-icon]:hidden!"],
				action: () => {
					setGroupsUnderneathExpanded(node, api, true);
				},
			},
			{
				name: "Collapse group",
				cssClasses: ["[&_.ag-menu-option-icon]:hidden!"],
				action: () => {
					setGroupsUnderneathExpanded(node, api, false);
				},
			},
		);
	}

	return items;
};
